import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import {
  ActivityOrderBy,
  Issue,
  IssueMessageType,
  replaceMentionPlaceholderWithNames,
  useTranslatedText,
} from '@gripp/shared-logic';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes';
import { getIssueAuditData, getIssueFullName } from '../issue/issueFormatUtils';
import { IssuesSeverityIcon } from '../shared/IssuesSeverityIcons';
import type { IssueCardProps } from './issueCard';

export const IssueCardOld = (props: IssueCardProps) => {
  const { t } = useTranslation();
  const { activity } = props;
  const [auditData, setAuditData] = useState({ who: '...', when: '...' });
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    setAuditData(
      getIssueAuditData(
        activity as unknown as Issue,
        props.filter.orderBy as ActivityOrderBy
      )
    );
  }, [activity, props.filter.orderBy]);

  const getProcessedTitle = () => {
    const titleText = activity.title;

    if (titleText.includes('@[') && (activity as any).userMentions) {
      return replaceMentionPlaceholderWithNames(
        titleText,
        (activity as any).userMentions,
        t('mentions.unknownUser'),
        true
      );
    }

    return titleText;
  };

  const getLatestMessageText = () => {
    const latestMessage = activity?.latestMessage;

    if (!latestMessage) {
      return '';
    }

    const user = getIssueFullName(
      latestMessage.createdBy?.id,
      latestMessage.createdBy?.name
    );

    if (props.isUnread && props.isMentionedInInbox) {
      return (
        <Text style={{ color: Colors.primary, fontSize: 14 }}>
          {t('activity.list.youAreMentioned')}
        </Text>
      );
    }

    if (
      latestMessage.type === IssueMessageType.RoutineMessage ||
      latestMessage.type === IssueMessageType.RoutineExceptionMessage
    ) {
      return activity?.subtitle
        ? `${user} ${activity.subtitle}`
        : t('routines.message.messageTitle', { name: user }).slice(0, -1);
    }

    if (
      latestMessage.type === IssueMessageType.SystemMessage ||
      latestMessage.type === IssueMessageType.RoutinePromptMessage
    ) {
      const { translatedText } = useTranslatedText('text', latestMessage);
      return translatedText;
    }

    if (!latestMessage.text) {
      return `${user}: ${t('issue.latestMessage.media')}`;
    }

    const { translatedText } = useTranslatedText('text', latestMessage);
    const messageText = translatedText || latestMessage?.text;

    const processedText = messageText.includes('@[')
      ? replaceMentionPlaceholderWithNames(
          messageText,
          latestMessage?.userMentions,
          t('mentions.unknownUser'),
          true
        )
      : messageText;

    return `${user}: ${processedText}`;
  };

  const latestMessageContent = getLatestMessageText();

  const renderLatestMessage = (textStyle: any, numberOfLines = 1) => {
    if (typeof latestMessageContent === 'string') {
      return (
        <Text variant="bodyMedium" style={textStyle} numberOfLines={numberOfLines}>
          {latestMessageContent}
        </Text>
      );
    }

    return latestMessageContent;
  };

  const hoverProps =
    Platform.OS === 'web'
      ? {
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
        }
      : {};

  return (
    <View style={styles.card} {...hoverProps}>
      <View style={[styles.row, { justifyContent: 'space-between' }]}>
        <View style={[styles.row, { flex: 1 }]}>
          <IssuesSeverityIcon severity={activity.severity} />
          <View
            style={[
              styles.row,
              styles.subHeaderContainer,
              isHovered && { backgroundColor: Colors.white },
              {
                ...Platform.select({
                  web: { maxWidth: '95%' },
                  default: { maxWidth: '90%' },
                }),
              },
            ]}
          >
            <MaterialCommunityIcons name="qrcode" color={Colors.grayText} size={14} />
            <Text
              variant="bodyMedium"
              style={[styles.detailText, styles.details]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {activity.asset?.name || activity.workspace.name}
            </Text>
          </View>
        </View>

        <View style={[styles.row, styles.whenBg]}>
          <Text variant="bodyMedium" style={styles.detailText}>
            {auditData.when}
          </Text>
        </View>
      </View>

      <View style={styles.content}>
        <View style={styles.row}>
          {activity.nextReminderDate && (
            <MaterialIcons
              name="alarm-on"
              size={16}
              style={{ color: Colors.grayText, paddingRight: 8 }}
            />
          )}
          <Text style={styles.title} numberOfLines={1}>
            {getProcessedTitle()}
          </Text>

          <View style={styles.indicatorIconContainer}>
            {activity.isPinned && (
              <MaterialCommunityIcons
                name="pin"
                color={Colors.black}
                size={16}
                style={styles.pinnedIcon}
              />
            )}

            {props.isUnread && <View style={styles.unreadDot} />}
          </View>
        </View>

        <View style={styles.row}>{renderLatestMessage(styles.lastMessage, 1)}</View>

        {activity.labels && activity.labels.length > 0 && (
          <View style={styles.labelsContainer}>
            {activity.labels.map((label) => (
              <View key={label.id} style={styles.labelPill}>
                <Text style={styles.labelText}>{label.name}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 0,
    width: '100%',
    alignSelf: 'stretch',
    padding: 16,
    height: 'auto',
    ...Platform.select({
      web: { gap: 12 },
      default: {
        gap: 10,
      },
    }),
  },
  content: {
    flexDirection: 'column',
    ...Platform.select({
      web: { gap: 4 },
      default: {
        gap: 2,
      },
    }),
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  title: {
    fontSize: 16,
    color: Colors.black,
    fontWeight: '500',
    flex: 1,
    marginRight: 8,
  },
  whenBg: {
    paddingHorizontal: 8,
    borderRadius: 100,
    zIndex: 2,
    marginLeft: 20,
  },
  details: {
    color: Colors.grayText,
  },
  detailText: {
    fontSize: 14,
    fontWeight: '500',
    letterSpacing: -0.4,
    lineHeight: 16,
  },
  subHeaderContainer: {
    overflow: 'hidden',
    backgroundColor: Colors.primaryGrayBackground,
    paddingHorizontal: 4,
    borderRadius: 4,
    gap: 4,
    height: 20,
  },
  lastMessage: {
    fontSize: 16,
    fontWeight: '400',
    color: Colors.grayText,
  },
  indicatorIconContainer: {
    marginLeft: 'auto',
    alignItems: 'center',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'row',
    gap: 5,
  },
  pinnedIcon: {
    width: 16,
    height: 16,
    backfaceVisibility: 'hidden',
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: Colors.primary,
    zIndex: 10,
    marginRight: 5,
  },
  labelsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4,
  },
  labelPill: {
    backgroundColor: Colors.primaryGrayBackground,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 15,
    alignSelf: 'flex-start',
  },
  labelText: {
    fontSize: 14,
    lineHeight: 16,
    color: Colors.black,
    fontWeight: '400',
  },
});


